/* forecastWizardRows.js -- the Forecast Wizard's ROW rules: how a vendor becomes an editable grid row, when a row is
 * "done", and how an edited row becomes the vendor object the host's onSaveVendors receives.
 *
 * MOVED HERE UNCHANGED from pages/ForecastWizard.jsx (2026-10-29, the vendor-forecast file) so a second surface -- the
 * Step-6 "fill from a file" upload -- builds its vendor objects through the SAME code the grid's Apply uses, rather
 * than a lookalike: the file patches a grid row, buildVendorObj turns it into the object, and the host's fold
 * (AdminPage step6SaveForecastRows) folds it. ForecastWizard imports every function below; nothing here changed
 * behaviour (the comments were made ASCII). */
import { deriveAnchorCalMonth } from './vendorForecast';
import { percentText } from './percentText';

const r2 = n => Math.round(n * 100) / 100;

// Per-vendor historical context over the actuals window (<= cutoff).
export function vendorContext(vendor, actualsMonths) {
  const md = vendor.monthlyData || {};
  const last12 = actualsMonths.slice(-12);
  const last3 = actualsMonths.slice(-3);
  const active12 = last12.some(m => (md[m] || 0) !== 0); // 12-mo dormancy line -- catches annuals
  const avg3 = last3.length ? r2(last3.reduce((s, m) => s + (md[m] || 0), 0) / 3) : 0;
  const cutoffMonth = actualsMonths[actualsMonths.length - 1];
  const cutoffVal = r2(md[cutoffMonth] || 0);
  const series = actualsMonths.slice(-18).map(m => md[m] || 0);
  return { active12, avg3, cutoffVal, series };
}

// Prefill amount for CONSTANT vendors only (pct/per_seat/one_off keep their
// existing config -- the rule never fabricates a % or $/seat).
export function prefillConstantAmount(rule, ctx) {
  if (rule === "zero") return 0;
  if (rule === "cutoff_value") return ctx.cutoffVal !== 0 ? ctx.cutoffVal : 0;
  return ctx.active12 ? ctx.avg3 : 0; // three_mo_avg (default)
}

// -- done-detection (2b-ii; cat-1 fix #2: TYPE-AWARE) --
// "Done" (forecast saved) is detected per forecast TYPE, because the types persist
// different things:
//   - constant / one_off -> >=1 forecast-month vmas row. The constant save 0-fills
//     every forecast month, so a saved constant (incl. confirmed-$0 dormant) carries
//     forecast keys; one_off writes no vmas (config-only carve-out) -> stays todo;
//     never-touched vendors carry none -> todo.
//   - pct_revenue -> CONFIG, not forecast vmas: done once a % is entered (pctRate
//     nonzero) OR a deliberate 0 paired with a chosen source. (Was always-todo
//     under the vmas-only rule -- the TODO-revert bug.)
//   - per_seat -> CONFIG: done once per_seat_cost is set (nonzero).
// DUAL-READ camel ?? snake (mirrors buildRows / Phase 1's helper).
export const isDone = (vendor, cutoff, hasSourceOptions = true) => {
  const type = vendor.type || vendor.forecast_type || "constant";
  if (type === "pct_revenue") {
    /* 4b -- A % VENDOR IS NEVER "SET" WHILE THERE IS NOTHING TO SET IT TO.
     * With an EMPTY source list the picker offers exactly one option, the
     * __total_revenue__ sentinel, which stored NULL -- and computeVendor read
     * NULL as rate x TOTAL REVENUE. Measured on cid=8's four rows that is
     * $7,537,323.68 of phantom expense over 9 months. The old rule returned true on a
     * RATE ALONE, so such a row showed the green "Set" badge and counted toward
     * "N of M set": the screen actively told the operator a mis-booked row was done.
     * KEYED ON WHETHER A CHOICE EXISTS, NOT ON THE SOURCE BEING NULL. When options
     * exist, nothing changes. */
    if (!hasSourceOptions) return false;
    const pr = vendor.pctRate != null ? vendor.pctRate : vendor.pct_rate;
    const src = vendor.pctRevenueSource != null ? vendor.pctRevenueSource : vendor.pct_revenue_source;
    // W-073 (b): the base must be ANSWERED (a line, or the stored Total Revenue sentinel); NULL is "never answered".
    return pr != null && src != null && src !== "";
  }
  if (type === "per_seat") {
    const psc = vendor.perSeatCost != null ? vendor.perSeatCost : vendor.per_seat_cost;
    return Number(psc) > 0;
  }
  // constant + one_off
  return Object.keys(vendor.monthlyData || {}).some(m => m > cutoff);
};
// The saved forecast amount = the first NONZERO forecast value (the booked
// per-period amount); its month gives the billing anchor for quarterly/annual.
// All-zero forecast (confirmed-$0 dormant) -> null.
export function firstNonzeroForecast(vendor, cutoff) {
  const md = vendor.monthlyData || {};
  const months = Object.keys(md).filter(m => m > cutoff && md[m] !== 0).sort();
  return months.length ? { month: months[0], amount: md[months[0]] } : null;
}

/* ONE vendor -> its grid edit-state row (the body of ForecastWizard's buildRows). 2b-ii -- DONE vendors load their SAVED
 * forecast (don't re-prefill); TODO vendors get the prefill rule. constant config (freq/start/end/anchor) + pct/per_seat
 * config load from the vendor either way. */
export function buildWizardRow(v, { ctx, rule, cutoff, firstForecast, hasSourceOptions, pctRevenueSourceOptions }) {
  const done = isDone(v, cutoff, hasSourceOptions);
  // DUAL-READ camel ?? snake (mirrors Phase 1's helper).
  const type = v.type || v.forecast_type || "constant";
  const baseAmount = v.amount != null ? v.amount : (v.monthly_amount != null ? v.monthly_amount : 0);
  let anchorMM = deriveAnchorCalMonth(v, firstForecast) || (firstForecast ? Number(firstForecast.split("-")[1]) : 1);
  let amount;
  if (type === "constant") {
    if (done) {
      // load the SAVED forecast: first nonzero forecast value = the booked
      // amount; its month = the billing anchor. all-zero -> confirmed-$0.
      const fnz = firstNonzeroForecast(v, cutoff);
      amount = fnz ? fnz.amount : 0;
      if (fnz) anchorMM = Number(fnz.month.split("-")[1]);
    } else {
      amount = prefillConstantAmount(rule, ctx); // todo -> prefill
    }
  } else {
    amount = Number(baseAmount) || 0;
  }
  return {
    type,
    freq: v.freq || v.billing_frequency || "monthly",
    amount,
    startDate: v.startDate || v.start_date || firstForecast,
    endDate: v.endDate || v.end_date || "until_canceled",
    anchorMM,
    pctRate: (v.pctRate != null ? v.pctRate : v.pct_rate) || 0,
    // B5: the rate cell's text -- a stored pct_revenue vendor shows its percent (0.0295 -> "2.95", 0.73 -> "73", a stored 0 -> "0");
    // a row switched to % of Revenue from another type starts BLANK and is required (it has no rate answer yet).
    pctText: (v.type || v.forecast_type) === "pct_revenue" && (v.pctRate != null ? v.pctRate : v.pct_rate) != null
      ? percentText(v.pctRate != null ? v.pctRate : v.pct_rate) : "",
    pctRevenueSource: (v.pctRevenueSource != null ? v.pctRevenueSource : v.pct_revenue_source) || null,
    perSeatCost: (v.perSeatCost != null ? v.perSeatCost : v.per_seat_cost) || 0,
    seatBasis: v.seatBasis || v.seat_basis || "fte",
    seatDepartments: v.seatDepartments || v.seat_departments || [],
    sourceOptions: pctRevenueSourceOptions || [],
  };
}

// Build the persist-ready vendor object for a checked row: spread the ORIGINAL
// vendor (preserves notes/active/contractor/etc. that save()'s dbPayload needs)
// + apply the row's edited config. Non-monthly constant carries the resolved
// billing anchor (YYYY-MM) so synthesizeForecastVmas books on the right cadence.
export function buildVendorObj(vendor, row, forecastMonths) {
  const obj = {
    ...vendor,
    type: row.type, freq: row.freq, amount: row.amount,
    pctRate: row.pctRate, pctRevenueSource: row.pctRevenueSource,
    perSeatCost: row.perSeatCost, seatBasis: row.seatBasis, seatDepartments: row.seatDepartments,
    startDate: row.startDate, endDate: row.endDate,
  };
  if (obj.type === "constant" && obj.freq !== "monthly") {
    obj.billingAnchor = forecastMonths.find(m => Number(m.split("-")[1]) === row.anchorMM) || forecastMonths[0];
  }
  return obj;
}
